// providers/TableUIProvider.tsx
import React, { createContext, useContext, useState, useRef, useCallback } from 'react';

interface DeleteConfirmModal {
    isOpen: boolean;
    shipmentId: string | null;
    shipmentReference: string | null;
}

interface TableUIContextType {
    // Search state
    searchTerm: string;
    searchResults: any[];
    currentSearchIndex: number;
    setSearchTerm: (term: string) => void;
    setSearchResults: (results: any[]) => void;
    setCurrentSearchIndex: (index: number) => void;

    // Sidebar state
    isSidebarOpened: boolean;
    selectedRow: any;
    setSidebarOpened: (open: boolean) => void;
    setSelectedRow: (row: any) => void;

    // Delete modal state
    deleteConfirmModal: DeleteConfirmModal;
    openDeleteConfirmModal: (shipmentId: string, reference: string) => void;
    closeDeleteConfirmModal: () => void;
    confirmDelete: (callback: (shipmentId: string) => void) => void;

    // Refs
    hotRef: React.MutableRefObject<any>;
    searchTimeoutRef: React.MutableRefObject<NodeJS.Timeout | null>;
}

const TableUIContext = createContext<TableUIContextType | undefined>(undefined);

export const useTableUI = () => {
    const context = useContext(TableUIContext);
    if (!context) {
        throw new Error('useTableUI must be used within TableUIProvider');
    }
    return context;
};

interface TableUIProviderProps {
    children: React.ReactNode;
}

export const TableUIProvider: React.FC<TableUIProviderProps> = ({ children }) => {
    const hotRef = useRef<any>(null);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Search state
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [currentSearchIndex, setCurrentSearchIndex] = useState<number>(-1);

    // Sidebar state
    const [isSidebarOpened, setSidebarOpened] = useState<boolean>(false);
    const [selectedRow, setSelectedRow] = useState<{ rowData: any[], index: number } | null>(null);

    // Delete modal state
    const [deleteConfirmModal, setDeleteConfirmModal] = useState<DeleteConfirmModal>({
        isOpen: false,
        shipmentId: null,
        shipmentReference: null,
    });

    // Delete modal handlers
    const openDeleteConfirmModal = useCallback((shipmentId: string, reference: string) => {
        setDeleteConfirmModal({
            isOpen: true,
            shipmentId,
            shipmentReference: reference,
        });
    }, []);

    const closeDeleteConfirmModal = useCallback(() => {
        setDeleteConfirmModal({
            isOpen: false,
            shipmentId: null,
            shipmentReference: null,
        });
    }, []);


    const confirmDelete = useCallback(async (callback: (shipmentId: string) => void) => {
        if (!deleteConfirmModal.shipmentId) return;
        const shipmentId = deleteConfirmModal.shipmentId;
        closeDeleteConfirmModal();
        await callback(shipmentId);
    }, [deleteConfirmModal.shipmentId, closeDeleteConfirmModal]);

    const contextValue: TableUIContextType = {
        // Search
        searchTerm,
        searchResults,
        currentSearchIndex,
        setSearchTerm,
        setSearchResults,
        setCurrentSearchIndex,

        // Sidebar
        isSidebarOpened,
        selectedRow,
        setSidebarOpened,
        setSelectedRow,

        // Delete modal
        deleteConfirmModal,
        openDeleteConfirmModal,
        closeDeleteConfirmModal,
        confirmDelete,

        // Refs
        hotRef,
        searchTimeoutRef,
    };

    return <TableUIContext.Provider value={contextValue}>{children}</TableUIContext.Provider>;
};